import { API, PolicyMigrationPreview, PolicyMigrationWrite, PolicyWorksheetViewDTO } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { POLICY_NEW_FIELD_MAPKEYS, PolicyFieldCandidate, PolicyNeedsReviewGroup, PolicyReviewGroup } from "@swivl-mono/types";
import { Button, ButtonVariant, Spinner } from "@swivl-mono/ui";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { NeedsReviewList } from "../../DocumentIngest/PolicyNeedsReviewList";
import { PolicyReviewHandlers, PolicyUpdatesList } from "../../DocumentIngest/PolicyUpdatesList";
import { PolicyWalkThrough } from "../../DocumentIngest/PolicyWalkThrough";
import { buildLocationOptions, buildPolicyReviewData, reviewKeyFor } from "./PoliciesTab.Adapter";
import { ScanIncompleteNotice } from "./ScanIncompleteNotice";
import { StepApplyBar, useStepApply } from "./StepApplyBar";
import { MigrationStepHandle, StepApplyOutcome } from "./workflow";

interface PoliciesTabProps {
  orgId: number;
  /** Called after this step writes, so the workflow can refresh steps that read what it wrote. */
  onApplied: () => void;
}

/** Group valid, non-null, non-excluded candidates into per-(scope, worksheet) answer writes for apply. */
function collectWrites(preview: PolicyMigrationPreview): PolicyMigrationWrite[] {
  const writes: PolicyMigrationWrite[] = [];
  const add = (locationId: number | undefined, candidates: PolicyFieldCandidate[]): void => {
    const byPolicyKey = new Map<string, Record<string, string>>();
    for (const c of candidates) {
      if (c.status !== "valid" || c.proposedValue === null || c.excluded === true) continue;
      const dot = c.mapKey.indexOf(".");
      const policyKey = c.mapKey.slice(0, dot);
      const itemKey = c.mapKey.slice(dot + 1);
      const answers = byPolicyKey.get(policyKey) ?? {};
      answers[itemKey] = c.proposedValue;
      byPolicyKey.set(policyKey, answers);
    }
    for (const [policyKey, answers] of byPolicyKey) writes.push(locationId ? { locationId, policyKey, answers } : { policyKey, answers });
  };
  add(undefined, preview.org);
  for (const loc of preview.locations) add(loc.locationId, loc.candidates);
  return writes;
}

// Stable reference so PolicyReviewHandlers doesn't get a fresh Set every render — this tab has no
// in-flight per-row mutations (every edit here is a synchronous local patch, never async).
const EMPTY_GROUP_KEYS: Set<string> = new Set();

/**
 * Step 2 — review the legacy → Policy-worksheet migration for the whole org in one combined list
 * (org values, plus per-location differences nested/expandable inline), using the bulk-import
 * Policies review components (PolicyUpdatesList/NeedsReviewList/PolicyWalkThrough) — exactly one
 * instance of each, matching BulkImportPage.tsx's PoliciesScreen shape, instead of an "Organization
 * section + one section per location" that duplicated every override (once nested/collapsed in the
 * org row's expand panel, and again as its own full top-level row).
 * Edits are held locally and written by this step's own `StepApplyBar`, which also opens the operator
 * ticket for the new v3 settings. "Approve"/"Skip" here are local review state only, with no per-row
 * server writes.
 */
export const PoliciesTab = forwardRef<MigrationStepHandle, PoliciesTabProps>(function PoliciesTab({ orgId, onApplied }, ref) {
  const [preview, setPreview] = useState<PolicyMigrationPreview | null>(null);
  const [worksheets, setWorksheets] = useState<PolicyWorksheetViewDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showChanges, setShowChanges] = useState(false);
  const [reviewedKeys, setReviewedKeys] = useState<Set<string>>(new Set());
  const [walkThroughOpen, setWalkThroughOpen] = useState(false);
  const [walkThroughStartKey, setWalkThroughStartKey] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, ws] = await Promise.all([API.Cortex.previewPolicyMigration(orgId), API.PolicyWorksheets.getWorksheets(orgId)]);
      setPreview(p);
      setWorksheets(ws.worksheets);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    reload().catch(() => {
      /* handled in reload */
    });
  }, [reload]);

  // Every per-candidate mutation below is a local, synchronous state patch — nothing here writes to
  // the server; only this step's Apply does that.
  const patchCandidate = useCallback((locationId: number | null, mapKey: string, patch: Partial<PolicyFieldCandidate>): void => {
    setPreview((prev) => {
      if (!prev) return prev;
      const apply = (list: PolicyFieldCandidate[]): PolicyFieldCandidate[] => list.map((c) => (c.mapKey === mapKey ? { ...c, ...patch } : c));
      if (locationId === null) return { ...prev, org: apply(prev.org) };
      return { ...prev, locations: prev.locations.map((l) => (l.locationId === locationId ? { ...l, candidates: apply(l.candidates) } : l)) };
    });
  }, []);

  const editCandidate = useCallback(
    (locationId: number | null, mapKey: string, value: string): Promise<void> => {
      patchCandidate(locationId, mapKey, { proposedValue: value });
      return Promise.resolve();
    },
    [patchCandidate]
  );

  // The "Skip"/"Include" toggle — collectWrites drops an excluded candidate, so this is the one
  // mutation here that actually changes what gets written when Policies is applied. Also persisted server-side
  // (fire-and-forget — the local patch above already reflects the decision immediately regardless of
  // whether the write succeeds), so a skip survives a reload instead of the field reappearing on the
  // next preview looking like it was never reviewed.
  const setExcluded = useCallback(
    (locationId: number | null, mapKey: string, excluded: boolean): void => {
      patchCandidate(locationId, mapKey, { excluded });
      void API.Cortex.setPolicyFieldExcluded(orgId, locationId, mapKey, excluded);
    },
    [patchCandidate, orgId]
  );

  // AffectedLocationsField's "Add locations" — gives a location its own override, seeded from the
  // org row's current proposed value when one exists (editable afterward like any other override).
  // For a genuine `scope: "locations"` group (no org candidate at all), `orgCandidate` is undefined
  // and every field just falls back to a blank/neutral default.
  const addLocationOverride = useCallback(
    (targetLocationId: number, mapKey: string, initialValue: string): void => {
      setPreview((prev) => {
        if (!prev) return prev;
        const orgCandidate = prev.org.find((c) => c.mapKey === mapKey);
        const locIndex = prev.locations.findIndex((l) => l.locationId === targetLocationId);
        if (locIndex === -1) return prev;
        const loc = prev.locations[locIndex];
        if (loc.candidates.some((c) => c.mapKey === mapKey)) return prev; // already has its own override
        const newCandidate: PolicyFieldCandidate = {
          mapKey,
          scope: { orgId, locationId: targetLocationId },
          proposedValue: initialValue,
          rawExtracted: orgCandidate?.rawExtracted ?? "",
          confidence: orgCandidate?.confidence ?? 1,
          source: orgCandidate?.source ?? { docId: 0, snippet: "" },
          status: "valid",
          diff: "changed",
          currentValue: null
        };
        const nextLocations = [...prev.locations];
        nextLocations[locIndex] = { ...loc, candidates: [...loc.candidates, newCandidate] };
        return { ...prev, locations: nextLocations };
      });
    },
    [orgId]
  );

  // The Approve button's cosmetic "reviewed" marker — feeds orgApplied/applied so the row shows
  // "Approved." Never removes the candidate from collectWrites's output, never persists beyond the
  // session. Takes already-built review keys and commits them in one Set update — building the full
  // list up front (see handleApprove) avoids one full Set copy per location for a group that spans
  // many of them.
  const markReviewed = useCallback((keys: string[]): void => {
    setReviewedKeys((prev) => {
      const next = new Set(prev);
      for (const key of keys) next.add(key);
      return next;
    });
  }, []);

  // Approving a group marks every scope it actually covers as reviewed in one click — the org scope
  // itself (when scope: "org"), plus every non-excluded location it carries (whether that's the
  // enriched overrides on an org-scope group, or the full membership of a genuine locations-scope
  // group). There's no separate per-location Approve control anymore, so this is the only Approve
  // affordance for both org and location state.
  const handleApprove = useCallback(
    (group: PolicyReviewGroup, childMapKeys: string[] = []): Promise<void> => {
      const mapKeys = [group.mapKey, ...childMapKeys];
      const keys: string[] = [];
      if (group.scope === "org") for (const mapKey of mapKeys) keys.push(reviewKeyFor(null, mapKey));
      for (const loc of group.locations) if (!loc.excluded) for (const mapKey of mapKeys) keys.push(reviewKeyFor(loc.locationId, mapKey));
      markReviewed(keys);
      return Promise.resolve();
    },
    [markReviewed]
  );

  // `locationIds` non-null only ever comes from AffectedLocationsField's chip, which always supplies
  // explicit ids regardless of the group's scope. `locationIds === null` only ever fires for the
  // org-scope Skip/Include button (PolicyUpdatesList only calls it with null when
  // `group.scope === "org"`), so it's unconditionally an org-scope edit.
  const handleToggleExcluded = useCallback(
    (group: PolicyReviewGroup, locationIds: number[] | null, excluded: boolean): void => {
      if (locationIds && locationIds.length > 0) {
        for (const id of locationIds) setExcluded(id, group.mapKey, excluded);
        return;
      }
      setExcluded(null, group.mapKey, excluded);
    },
    [setExcluded]
  );

  // NeedsReviewList's "Dismiss" — sometimes the extraction is just wrong and there's nothing to fix,
  // only to make go away. Reuses the exact same persisted exclusion `setExcluded` already writes for
  // the main review rows' Skip/Include; a dismissed needs-review card just has no proposed value to
  // keep showing once excluded, so it disappears from the list instead of staying visible-but-skipped.
  const dismissNeedsReview = useCallback(
    (group: PolicyNeedsReviewGroup): void => {
      if (group.scope === "locations") {
        for (const loc of group.locations) setExcluded(loc.locationId, group.mapKey, true);
        return;
      }
      setExcluded(null, group.mapKey, true);
    },
    [setExcluded]
  );

  // `editLocationId` present = a single location's own edit (from the expand panel). Absent +
  // `scope === "org"` = the org row's own value. Absent + `scope === "locations"` (no org candidate
  // for this mapKey at all) = a group-wide edit, fanned out to every non-excluded member — only
  // reachable when they already agree (PolicyUpdateRow gates this control on `proposedValue !== null`),
  // so "every member" and "the one value being typed" are the same edit.
  const handleEditValue = useCallback(
    (group: PolicyReviewGroup, newValue: string, editLocationId?: number): void => {
      if (editLocationId !== undefined) {
        void editCandidate(editLocationId, group.mapKey, newValue);
        return;
      }
      if (group.scope === "org") {
        void editCandidate(null, group.mapKey, newValue);
        return;
      }
      for (const loc of group.locations) if (!loc.excluded) void editCandidate(loc.locationId, group.mapKey, newValue);
    },
    [editCandidate]
  );

  const handleAddLocations = useCallback(
    (group: PolicyReviewGroup, locationIds: number[]): void => {
      for (const id of locationIds) addLocationOverride(id, group.mapKey, group.proposedValue ?? "");
    },
    [addLocationOverride]
  );

  const handlers: PolicyReviewHandlers = useMemo(
    () => ({
      onToggleExcluded: handleToggleExcluded,
      onEditValue: handleEditValue,
      onAddLocations: handleAddLocations,
      onApprove: handleApprove,
      busyGroupKeys: EMPTY_GROUP_KEYS,
      approving: false
    }),
    [handleToggleExcluded, handleEditValue, handleAddLocations, handleApprove]
  );

  // NeedsReviewList's inline fix — applies the corrected value to every scope that shared this exact
  // unresolved text: just org (scope: "org"), or every listed location (scope: "locations").
  const fixNeedsReview = useCallback(
    (group: PolicyNeedsReviewGroup, value: string): void => {
      const patch: Partial<PolicyFieldCandidate> = { proposedValue: value, status: "valid", reason: undefined };
      if (group.scope === "org") {
        patchCandidate(null, group.mapKey, patch);
        return;
      }
      for (const loc of group.locations) patchCandidate(loc.locationId, group.mapKey, patch);
    },
    [patchCandidate]
  );

  // NeedsReviewList's per-location fix — corrects just ONE location within a `scope: "locations"`
  // card, independently of its siblings (the per-location breakdown's own control).
  const fixNeedsReviewLocation = useCallback(
    (group: PolicyNeedsReviewGroup, locationId: number, value: string): void => {
      patchCandidate(locationId, group.mapKey, { proposedValue: value, status: "valid", reason: undefined });
    },
    [patchCandidate]
  );

  const willWriteCount = useMemo(() => (preview ? collectWrites(preview).reduce((n, w) => n + Object.keys(w.answers).length, 0) : 0), [preview]);

  const handleApply = useCallback(async (): Promise<StepApplyOutcome> => {
    if (!preview) return { message: "nothing to write" };
    const res = await API.Cortex.applyPolicyMigration(orgId, collectWrites(preview));
    onApplied();
    const message = `${res.appliedFieldCount} answer(s) written${res.failedFieldCount > 0 ? `, ${res.failedFieldCount} failed` : ""}`;
    // The operator ticket for the new v3 settings is a side effect of applying Policies. The answers
    // are already written by now, so a ticket failure is reported, never thrown.
    let outcome: StepApplyOutcome;
    try {
      const ticket = await API.Cortex.generatePolicyNewFieldsTicket(orgId);
      outcome = {
        message: ticket.ticketNumber == null ? message : `${message}; new-settings ticket ${ticket.created ? "opened" : "already open"}`,
        ticketNumber: ticket.ticketNumber
      };
    } catch (err) {
      outcome = { message, warning: `The new-settings ticket could not be opened: ${err instanceof Error ? err.message : String(err)}` };
    }
    await reload();
    return outcome;
  }, [orgId, preview, onApplied, reload]);

  useImperativeHandle(ref, () => ({ reload }), [reload]);
  const applyState = useStepApply(handleApply);

  // Built once per (preview, worksheets, reviewedKeys) change — a plain data transform (see
  // PoliciesTab.Adapter's buildPolicyReviewData), covering org AND every location's overrides in one
  // combined result — no more separate org/location sections.
  const reviewData = useMemo(() => (preview ? buildPolicyReviewData(preview, worksheets, reviewedKeys) : null), [preview, worksheets, reviewedKeys]);

  // Every location known to have at least one override anywhere in this org's preview — the pool the
  // locations chip offers to add/toggle from.
  const allLocationOptions = useMemo(() => (preview ? buildLocationOptions(preview.locations) : []), [preview]);

  if (loading || !preview || !reviewData) {
    return (
      <div className="body-sm mx-auto flex max-w-5xl items-center gap-2 p-6 text-grey-400">
        <Spinner /> Loading policy migration preview…
      </div>
    );
  }
  if (error) return <div className="body-sm mx-auto max-w-5xl p-6 text-red-500">Failed to load policy preview: {error}</div>;

  const handleRetryScan = (): void => {
    reload().catch(() => {
      /* handled in reload */
    });
  };

  const openWalkThroughAt = (groupKey: string | null): void => {
    setWalkThroughStartKey(groupKey);
    setWalkThroughOpen(true);
  };

  return (
    <>
      <StepApplyBar
        description={`${willWriteCount} answer(s) will be written, and the operator is ticketed for the ${POLICY_NEW_FIELD_MAPKEYS.length} new v3 setting(s).`}
        applyLabel="Apply Policies"
        confirmText={`This writes the reviewed Policy answers for org ${orgId} and opens (or keeps) the operator ticket for the new v3 settings. Safe to re-run.`}
        state={applyState}
      />
      <div className="mx-auto max-w-5xl p-6">
        <div className="mb-6">
          <h2 className="heading-base">Policies to migrate</h2>
          <p className="body-sm mt-1 text-grey-500">Review the proposed answers — locations that differ from the org value expand inline.</p>
        </div>

        {preview.scanIncomplete && <ScanIncompleteNotice onRetry={handleRetryScan} />}

        {preview.lastRun && (
          <div className="mb-6 rounded border border-grey-200 bg-white p-3">
            <div className="body-sm flex items-center justify-between text-grey-600">
              <span>
                Last applied {new Date(preview.lastRun.at).toLocaleString()} — wrote {preview.lastRun.appliedFieldCount} field(s)
                {preview.lastRun.locationCount > 0 ? ` across ${preview.lastRun.locationCount} location override(s)` : ""}
                {preview.lastRun.failedFieldCount > 0 ? `, ${preview.lastRun.failedFieldCount} failed` : ""}.
              </span>
              {preview.lastRun.changes.length > 0 && (
                <Button variant={ButtonVariant.GHOST} onClick={() => setShowChanges((v) => !v)}>
                  {showChanges ? "Hide changes" : `View changes (${preview.lastRun.changes.length})`}
                </Button>
              )}
            </div>
            {showChanges && (
              <ul className="mt-3 divide-y divide-grey-100 rounded border border-grey-200">
                {preview.lastRun.changes.map((c) => (
                  <li key={`${c.scope}:${c.mapKey}`} className="body-xs flex items-center justify-between gap-4 px-3 py-1.5 text-grey-700">
                    <span>
                      <span className="text-grey-400">{c.scope}</span> · {c.mapKey}
                    </span>
                    <span className="truncate text-grey-500">{c.value}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3">
          {reviewData.groups.length > 0 && (
            <div className="flex justify-end">
              <div className="flex overflow-hidden rounded-[4px] border border-grey-300">
                <button
                  type="button"
                  onClick={() => setWalkThroughOpen(false)}
                  className={filterClasses(
                    "px-3 py-[5px] text-[13px] font-semibold whitespace-nowrap transition-colors",
                    !walkThroughOpen ? "bg-grey-900 text-white" : "bg-white text-grey-700 hover:bg-grey-50"
                  )}
                >
                  All updates
                </button>
                <button
                  type="button"
                  onClick={() => openWalkThroughAt(null)}
                  className={filterClasses(
                    "px-3 py-[5px] text-[13px] font-semibold whitespace-nowrap transition-colors",
                    walkThroughOpen ? "bg-grey-900 text-white" : "bg-white text-grey-700 hover:bg-grey-50"
                  )}
                >
                  Walk through
                </button>
              </div>
            </div>
          )}

          {reviewData.needsReview.length > 0 && (
            <NeedsReviewList
              groups={reviewData.needsReview}
              busyGroupKeys={EMPTY_GROUP_KEYS}
              onFix={fixNeedsReview}
              sourceLabel="Your legacy system"
              onFixLocation={fixNeedsReviewLocation}
              onDismiss={dismissNeedsReview}
            />
          )}

          {reviewData.groups.length === 0 && reviewData.needsReview.length === 0 ? (
            <p className="body-sm text-grey-500">No legacy policy values found for this org.</p>
          ) : reviewData.groups.length > 0 ? (
            <PolicyUpdatesList groups={reviewData.groups} allLocationOptions={allLocationOptions} handlers={handlers} />
          ) : null}

          {walkThroughOpen && reviewData.groups.length > 0 && (
            <PolicyWalkThrough
              groups={reviewData.groups}
              allLocationOptions={allLocationOptions}
              startGroupKey={walkThroughStartKey}
              handlers={handlers}
              onClose={() => setWalkThroughOpen(false)}
            />
          )}
        </div>
      </div>
    </>
  );
});
